/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, SubmitHandler } from 'react-hook-form'
import { errorToast, successToast } from 'src/utils/toastService'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'src/store'
import { startPageLoader, stopPageLoader } from 'src/store/features/common/commonSlice'
import {
  useGetNotificationByIdQuery,
  useUpdateNotificationMutation,
} from 'src/api/notificationsApi'
import { recipientsOptions } from 'src/utils/constants'

type IFormInput = {
  title: string
  scheduledTime: string
  repeatEvery: string
  details: string
  recipient: number
  status: string
  customRepeatDays?: number | null
}

const formSchema = Yup.object().shape({
  title: Yup.string().required('Yup is required'),
  scheduledTime: Yup.string().required('Scheduled Time is required'),
  repeatEvery: Yup.string().required('Select Interval is required'),
  details: Yup.string().required('Details is required'),
  recipient: Yup.number().required('Recipient is required'),
  status: Yup.string().required('Status is required'),
  customRepeatDays: Yup.number().when('repeatEvery', (repeatEvery: any) => {
    return repeatEvery?.[0] === 'custom'
      ? Yup.number().required('Custom Repeat Days is required')
      : Yup.number().notRequired()
  }),
})

type useEditNotificationProps = {
  successCallback: () => void
  id: number
}
const useEditNotification = ({ successCallback, id }: useEditNotificationProps) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'all',
    defaultValues: {
      title: '',
      scheduledTime: '',
      repeatEvery: '',
      details: '',
      recipient: 0,
      status:''
    },
  })
  const { data, isFetching, refetch } = useGetNotificationByIdQuery(id)

  const [updateNotification, { isLoading: isLoadingEditNotification }] =
    useUpdateNotificationMutation()
  const dispatch = useDispatch<AppDispatch>()

  const onSubmit: SubmitHandler<IFormInput> = (data) => {
    const formData: any = {
      title: data.title,
      status: data.status,
      scheduled_time: data.scheduledTime,
      content: data.details,
      target_user_types_ids: [],
      repeat_every: data.repeatEvery,
      custom_repeat_days: null,
    }

    // if repeat every field is custom
    if (data.repeatEvery === 'custom' && data.customRepeatDays) {
      formData.custom_repeat_days = data.customRepeatDays
    }

    if (data.recipient !== 0) {
      // if not selected all receipients
      formData.target_user_types_ids.push(data.recipient)
    } else {
      // if  selected all receipients
      recipientsOptions
        .filter((option) => option.value !== 0)
        .forEach((option) => {
          formData.target_user_types_ids.push(option.value)
        })
    }

    updateNotification({
      id,
      data: formData,
    })
      .unwrap()
      .then((res) => {
        console.log(res)
        successToast('Notification updated successfully.')
        reset()
        successCallback()
      })
      .catch((err) => {
        console.log(err)
        errorToast('Unable to updated notification.')
      })
  }
  useEffect(() => {
    if (isFetching) {
      dispatch(startPageLoader())
    } else {
      dispatch(stopPageLoader())
    }
  }, [isFetching])

  useEffect(() => {
    if (data) {
      setValue('title', data.title)
      setValue('details', data.content)
      setValue('scheduledTime', data.scheduled_time)
      setValue('recipient', data.target_user_types.length === 1 ? data.target_user_types[0].id : 0)
      setValue('repeatEvery', data.repeat_every)
      setValue('customRepeatDays', data.custom_repeat_days)
      setValue('status', data.status)
    }
  }, [data])

  return {
    repeatEvery: data?.repeat_every,
    onSubmit: handleSubmit(onSubmit),
    control,
    errors,
    isLoadingEditNotification,
  }
}

export default useEditNotification
